import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Car, Package, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SearchResult {
  id: string;
  type: "customer" | "vehicle" | "tire_set";
  title: string;
  subtitle: string;
  path: string;
}

/**
 * Inline header search for desktop. Renders an actual <input> the user can
 * type into directly (no dialog), with a results popover anchored beneath.
 */
export function SearchInline() {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!search || search.length < 2 || !user) {
      setResults([]);
      return;
    }
    setLoading(true);
    const term = `*${search}*`;
    const t = setTimeout(async () => {
      const all: SearchResult[] = [];
      try {
        const [{ data: customers }, { data: vehicles }, { data: tireSets }] = await Promise.all([
          supabase
            .from("customers")
            .select("id, name, email, phone")
            .or(`name.ilike.${term},email.ilike.${term},phone.ilike.${term}`)
            .limit(5),
          supabase
            .from("vehicles")
            .select("id, reg_number, make, model, customers!inner(id, name)")
            .or(`reg_number.ilike.${term},make.ilike.${term},model.ilike.${term}`)
            .limit(5),
          supabase
            .from("tire_sets")
            .select("id, season, qr_code, customers!inner(id, name), vehicles!inner(id, reg_number)")
            .or(`qr_code.ilike.${term}`)
            .limit(5),
        ]);
        customers?.forEach((c) =>
          all.push({
            id: c.id,
            type: "customer",
            title: c.name,
            subtitle: c.phone || c.email || "",
            path: `/customers`,
          })
        );
        vehicles?.forEach((v: any) =>
          all.push({
            id: v.id,
            type: "vehicle",
            title: v.reg_number,
            subtitle: `${v.make || ""} ${v.model || ""} - ${v.customers?.name || ""}`.trim(),
            path: `/customers`,
          })
        );
        tireSets?.forEach((ts: any) =>
          all.push({
            id: ts.id,
            type: "tire_set",
            title: `${ts.season} däck - ${ts.vehicles?.reg_number || ""}`,
            subtitle: ts.customers?.name || "",
            path: `/tires/${ts.id}`,
          })
        );
        setResults(all);
        setActiveIndex(0);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, user]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (r: SearchResult) => {
    setOpen(false);
    setSearch("");
    setResults([]);
    navigate(r.path);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) select(r);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const icon = (type: string) =>
    type === "customer" ? (
      <User className="h-4 w-4" />
    ) : type === "vehicle" ? (
      <Car className="h-4 w-4" />
    ) : (
      <Package className="h-4 w-4" />
    );

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-background-2 border border-border focus-within:border-accent transition-colors">
        <Search className="h-4 w-4 text-foreground-muted shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Sök kunder, däck, fordon…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-foreground-muted outline-none"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />}
      </div>

      {open && search.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-lg border border-border bg-card shadow-elevated overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="px-3 py-4 text-sm text-foreground-muted text-center">Inga resultat hittades.</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                select(r);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`w-full flex items-start gap-3 px-3 py-2 text-left transition-colors ${
                i === activeIndex ? "bg-accent-soft text-accent-ink" : "hover:bg-background-2 text-foreground"
              }`}
            >
              <span className="mt-0.5 text-foreground-muted">{icon(r.type)}</span>
              <span className="flex flex-col min-w-0">
                <span className="text-sm font-medium truncate">{r.title}</span>
                {r.subtitle && (
                  <span className="text-xs text-foreground-muted truncate">{r.subtitle}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}